// Postcode checker UI loader for Bottomsheet
// Consumes markup from snippets/bottomsheet_postcode_checker.liquid
// Requires: postcode-utils.js for validation and distance calculations
// Debug logging: Set DEBUG_MODE in postcode-utils.js to true/false to toggle logging

(function () {
    const DELIVERY_ANNOUNCEMENT_START = "00:01";
    const DELIVERY_ANNOUNCEMENT_END = "23:59";
    const COUNTDOWN_WINDOW_MINUTES = 59;

    function ensureStylesheet(sourceEl) {
        if (!sourceEl) return;
        const href = sourceEl.dataset.pcCss;
        if (!href) return;
        const existing = document.querySelector("link[data-pc-css]");
        if (existing) return;
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = href;
        link.setAttribute("data-pc-css", "true");
        document.head.appendChild(link);
    }

    function ensureBottomsheetInstance() {
        if (!window.bottomsheet && typeof Bottomsheet !== "undefined") {
            window.bottomsheet = new Bottomsheet();
        }
        return window.bottomsheet;
    }

    function extractPostcodeFromStorage(storedData) {
        if (!storedData) return null;
        try {
            const parsed = JSON.parse(storedData);
            return parsed.postcode || null;
        } catch (e) {
            // Legacy format - plain string
            return storedData;
        }
    }

    function getHeaderBannerZoneData(container) {
        if (!container) return null;

        const zoneScript = container.querySelector("[data-pc-banner-zones]");
        if (!zoneScript) return null;

        try {
            return JSON.parse(zoneScript.textContent || "{}");
        } catch (e) {
            debugWarn("[HEADER BANNER] Failed to parse banner zone data:", e);
            return null;
        }
    }

    function buildZoneDataFromHeader() {
        const containers = document.querySelectorAll(
            ".postcode-checker-container",
        );

        for (const container of containers) {
            const bannerData = getHeaderBannerZoneData(container);
            if (!bannerData) continue;

            return {
                deliveryZones: [
                    {
                        title: "Zone 1",
                        handle: "zone-1",
                        postcodes: bannerData.zone1Postcodes || [],
                    },
                    {
                        title: "Zone 2",
                        handle: "zone-2",
                        postcodes: bannerData.zone2Postcodes || [],
                    },
                    {
                        title: "Zone 3",
                        handle: "zone-3",
                        postcodes: bannerData.zone3Postcodes || [],
                    },
                ],
                postalZone: {
                    title: "Postal Zone",
                    handle: "postal-zone",
                    postcodes: bannerData.postalPostcodes || [],
                },
                blackoutPostcodes: {
                    title: "Blackout Postcodes",
                    handle: "blackout-postcodes",
                    postcodes: bannerData.blackoutPostcodes || [],
                },
            };
        }

        return {
            deliveryZones: [],
            postalZone: { title: "Postal Zone", handle: "postal-zone", postcodes: [] },
            blackoutPostcodes: {
                title: "Blackout Postcodes",
                handle: "blackout-postcodes",
                postcodes: [],
            },
        };
    }

    function shouldUseZone3PostalBannerMessage(postcode, container) {
        if (!postcode) return false;

        const zoneData = getHeaderBannerZoneData(container);
        if (!zoneData) return false;

        const outbound = getOutboundCode(postcode);
        const zone1Postcodes = zoneData.zone1Postcodes || [];
        const zone2Postcodes = zoneData.zone2Postcodes || [];
        const zone3Postcodes = zoneData.zone3Postcodes || [];
        const postalPostcodes = zoneData.postalPostcodes || [];

        const inZone1 = zone1Postcodes.includes(outbound);
        const inZone2 = zone2Postcodes.includes(outbound);
        const inZone3 = zone3Postcodes.includes(outbound);
        const inPostal =
            typeof isInPostalZone === "function"
                ? isInPostalZone(outbound, { postcodes: postalPostcodes })
                : postalPostcodes.some((code) => {
                      const prefix = (code || "").replace(/_$/, "");
                      const outboundPrefix = outbound.replace(/_$/, "");
                      return outboundPrefix.startsWith(prefix);
                  });

        debugLog("[HEADER BANNER] Message zone check", {
            outbound,
            inZone1,
            inZone2,
            inZone3,
            inPostal,
        });

        // Zone 1/2 are valid for next-day messaging, even if they also match postal zone.
        if (inZone1 || inZone2) return false;

        // Alternate message for zone-3 or postal-zone only when not in zone 1/2.
        return inZone3 || inPostal;
    }

    function applyHeaderAnnouncementMessage(postcode) {
        const containers = document.querySelectorAll(
            ".postcode-checker-container",
        );
        if (!containers.length) return;

        containers.forEach((container) => {
            const zoneData = getHeaderBannerZoneData(container) || {};
            const outbound = postcode ? getOutboundCode(postcode) : null;
            const blackoutZone = {
                postcodes: zoneData.blackoutPostcodes || [],
            };
            const isBlackout =
                !!outbound &&
                typeof isInBlackoutZone === "function" &&
                isInBlackoutZone(outbound, blackoutZone);

            const useZone3PostalMessage = shouldUseZone3PostalBannerMessage(
                postcode,
                container,
            );
            const selectedVariant = useZone3PostalMessage
                ? "zone3-postal"
                : "standard";

            const headerMessages = container.querySelectorAll(
                '.announcement-msg[data-zone-variant]',
            );
            headerMessages.forEach((el) => {
                const isActive = el.dataset.zoneVariant === selectedVariant;
                el.dataset.variantActive = isActive ? "true" : "false";
                if (!isActive) {
                    el.hidden = true;
                }
            });

            debugLog("[HEADER BANNER] Applied message variant:", {
                useZone3PostalMessage,
                selectedVariant,
                isBlackout,
            });

            // Hide topbar announcement message + divider for blackout postcodes.
            // Show them for all non-blackout postcodes.
            const messageElements = container.querySelectorAll(".postcode-msg");
            const dividerElements = container.querySelectorAll("#divider");
            messageElements.forEach((el) => {
                el.hidden = isBlackout;
            });
            dividerElements.forEach((el) => {
                el.hidden = isBlackout;
            });

            // Reinitialize announcement timing so the active variant updates immediately.
            initDeliveryAnnouncement(container);
        });
    }

    function initAnnouncementClose() {
        const closeIcons = document.querySelectorAll(
            ".close-icon, .desktop-close-icon",
        );
        const announcementElements = document.querySelectorAll(
            ".announcment-extra, #postcode-announcment, .desktop-announcment, .desktop-announcment-extra, .desktop-announcment-container",
        );
        const mobileAnnouncementElements = document.querySelectorAll(
            ".announcment-extra, #postcode-announcment",
        );
        const maxCloses = 3;

        const getCloseCount = () => {
            const legacyClosed =
                localStorage.getItem("pcc-announcement-closed") === "true";
            if (legacyClosed) return maxCloses;
            const raw = localStorage.getItem("pcc-announcement-close-count");
            const parsed = parseInt(raw || "0", 10);
            return Number.isNaN(parsed) ? 0 : parsed;
        };

        const setCloseCount = (count) => {
            localStorage.setItem("pcc-announcement-close-count", String(count));
            if (count >= maxCloses) {
                localStorage.setItem("pcc-announcement-closed", "true");
            }
        };

        const hideAnnouncement = () => {
            announcementElements.forEach((el) => {
                el.hidden = true;
            });
        };

        const hideMobileAnnouncementNoCount = () => {
            mobileAnnouncementElements.forEach((el) => {
                el.hidden = true;
            });
        };

        // Hide announcement elements if closed 3 times
        if (getCloseCount() >= maxCloses) {
            hideAnnouncement();
        }

        // Add close handlers
        closeIcons.forEach((icon) => {
            icon.addEventListener("click", (e) => {
                e.stopPropagation();
                const nextCount = Math.min(getCloseCount() + 1, maxCloses);
                setCloseCount(nextCount);
                hideAnnouncement();
            });
        });

        // Mobile-only: tap outside announcement content to hide it without increasing close count.
        document.addEventListener("click", (e) => {
            if (window.innerWidth >= 768) return;

            const target = e.target;
            const clickedInsideAnnouncement =
                target &&
                typeof target.closest === "function" &&
                target.closest("#postcode-announcment");

            if (clickedInsideAnnouncement) return;

            hideMobileAnnouncementNoCount();
        });

        // Mobile-only: scrolling should also dismiss the mobile announcement without counting as a close.
        window.addEventListener(
            "scroll",
            () => {
                if (window.innerWidth >= 768) return;
                hideMobileAnnouncementNoCount();
            },
            { passive: true },
        );
    }

    function updateHeaderPostcodeBanner() {
        const promptBanners = document.querySelectorAll(
            "#postcode-checker-prompt",
        );
        const hasPcBanners = document.querySelectorAll(
            "#postcode-checker-has-pc",
        );
        const desktopCarousels = document.querySelector("#desktop-carousels");

        // Parse stored postcode data
        const storedData = localStorage.getItem("pcc-valid-pc");
        const storedPc = storedData
            ? normalizePostcode(extractPostcodeFromStorage(storedData))
            : null;

        if (!promptBanners.length || !hasPcBanners.length) return;

        if (storedPc) {
            if (desktopCarousels) {
                desktopCarousels.style.visibility = "visible";
            }
            promptBanners.forEach((banner) => (banner.hidden = true));
            hasPcBanners.forEach((banner) => {
                banner.hidden = false;
                const postcodeText = banner.querySelector("#postcode");
                if (postcodeText) postcodeText.textContent = storedPc;
            });
            applyHeaderAnnouncementMessage(storedPc);
        } else {
            if (desktopCarousels) {
                desktopCarousels.style.visibility = "hidden";
            }
            promptBanners.forEach((banner) => (banner.hidden = false));
            hasPcBanners.forEach((banner) => (banner.hidden = true));
            applyHeaderAnnouncementMessage(null);
        }
    }

    function initCountdown(
        container,
        cutoffTime,
        defaultMsg,
        countdownMsg,
        orderByMsg,
    ) {
        if (!container || !cutoffTime) return;

        // Prevent multiple parallel timers on the same container.
        if (container.__pcCountdownTimer) {
            clearInterval(container.__pcCountdownTimer);
            container.__pcCountdownTimer = null;
        }

        const pad = (n) => n.toString().padStart(2, "0");
        const now = new Date();
        const currentTime = `${pad(now.getHours())}:${pad(now.getMinutes())}`;

        const showDefault = () => {
            if (defaultMsg) defaultMsg.hidden = false;
            if (countdownMsg) countdownMsg.hidden = true;
            if (orderByMsg) orderByMsg.hidden = true;
        };

        const showCountdown = () => {
            if (defaultMsg) defaultMsg.hidden = true;
            if (countdownMsg) countdownMsg.hidden = false;
            if (orderByMsg) orderByMsg.hidden = true;
        };

        const showOrderBy = () => {
            if (defaultMsg) defaultMsg.hidden = true;
            if (countdownMsg) countdownMsg.hidden = true;
            if (orderByMsg) orderByMsg.hidden = false;
        };

        const minutesSinceMidnight = now.getHours() * 60 + now.getMinutes();
        const [cutoffHour, cutoffMinute] = cutoffTime.split(":").map(Number);
        const cutoffMinutes = cutoffHour * 60 + cutoffMinute;

        const orderByStart = 1; // 12:01am
        // Build windows from cutoff dynamically:
        // - Order-by: start of day -> 60 mins before cutoff
        // - Countdown: final 60 mins before cutoff
        // - Default: after cutoff
        const orderByEnd = Math.max(cutoffMinutes - 60, orderByStart);
        const countdownStart = Math.max(cutoffMinutes - 59, orderByStart);
        const countdownEnd = cutoffMinutes;
        const defaultStart = cutoffMinutes + 1;

        if (
            minutesSinceMidnight >= orderByStart &&
            minutesSinceMidnight <= orderByEnd
        ) {
            // 12:01am - 60 mins before cutoff
            showOrderBy();
        } else if (
            minutesSinceMidnight >= countdownStart &&
            minutesSinceMidnight <= countdownEnd
        ) {
            // Final 60 mins before cutoff
            showCountdown();

            const today = now.toDateString();
            const cutoffDate = new Date(`${today} ${cutoffTime}`);
            const countdownEndTime = cutoffDate.getTime();

            // Update countdown immediately, then every second
            const updateCountdown = () => {
                const current = new Date().getTime();
                let distance = countdownEndTime - current;

                if (distance < 0) {
                    clearInterval(timer);
                    showDefault();
                    return;
                }

                const minutes = Math.floor(
                    (distance % (1000 * 60 * 60)) / (1000 * 60),
                );
                const countdownTextProductPage = `<span class="pc-product-content-bold">Order within ${minutes} mins</span> for <br/>earliest delivery and collection`;
                const countdownSuffix =
                    (countdownMsg && countdownMsg.dataset && countdownMsg.dataset.countdownSuffix) ||
                    "for next day delivery";
                const countdownText = `Order in <span class="pc-product-content-bold">${minutes} mins</span> ${countdownSuffix}`;
                const isProductPage =
                    container &&
                    (container.classList.contains("pc-product-container") ||
                        container.closest("pc-product-container"));
                if (countdownMsg) {
                    countdownMsg.innerHTML = isProductPage
                        ? countdownTextProductPage
                        : countdownText;
                }
            };

            updateCountdown(); // Call immediately
            const timer = setInterval(updateCountdown, 1000);
            container.__pcCountdownTimer = timer;
        } else if (
            minutesSinceMidnight >= defaultStart &&
            minutesSinceMidnight <= 23 * 60 + 59
        ) {
            // 4:01pm - 11:59pm
            showDefault();
        } else {
            if (defaultMsg) defaultMsg.hidden = true;
            if (countdownMsg) countdownMsg.hidden = true;
            if (orderByMsg) orderByMsg.hidden = true;
        }
    }

    function initDeliveryAnnouncement(targetContainer = null) {
        const containers = targetContainer
            ? [targetContainer]
            : Array.from(document.querySelectorAll(".postcode-checker-container"));
        if (!containers.length) return;

        const phoneEls = document.querySelectorAll(".announcement-phone");

        containers.forEach((container) => {
            if (!container) return;

            const cutoffTime = container.dataset.cutoffTime;
            if (!cutoffTime) return;

            const defaultMsg =
                container.querySelector(
                    '.announcement-msg[data-msg="default"][data-variant-active="true"]',
                ) || container.querySelector('.announcement-msg[data-msg="default"]');
            const countdownMsg =
                container.querySelector(
                    '.announcement-msg[data-msg="countdown"][data-variant-active="true"]',
                ) || container.querySelector('.announcement-msg[data-msg="countdown"]');
            const orderByMsg =
                container.querySelector(
                    '.announcement-msg[data-msg="order-by"][data-variant-active="true"]',
                ) || container.querySelector('.announcement-msg[data-msg="order-by"]');

            if (!defaultMsg || !countdownMsg) return;

            initCountdown(
                container,
                cutoffTime,
                defaultMsg,
                countdownMsg,
                orderByMsg,
            );
        });

        // Set phone numbers
        phoneEls.forEach((el) => (el.textContent = "03300589525"));
    }

    function buildContent(template, options = {}) {
        const { closeOnSubmitMobile = false } = options;
        const fragment = template.content.cloneNode(true);
        const wrapper = document.createElement("div");
        wrapper.appendChild(fragment);

        // Extract zone data from JSON script tag (legacy) or header banner data (source of truth)
        const zonesScript = wrapper.querySelector("[data-pc-zones]");
        const zoneData = zonesScript
            ? JSON.parse(zonesScript.textContent)
            : buildZoneDataFromHeader();
        const deliveryZones = zoneData.deliveryZones;
        const postalZone = zoneData.postalZone;
        const blackoutPostcodes = zoneData.blackoutPostcodes;

        const formView = wrapper.querySelector("[data-pc-form]");
        const successView = wrapper.querySelector("[data-pc-success]");
        const nationwideView = wrapper.querySelector("[data-pc-nationwide]");
        const failureView = wrapper.querySelector("[data-pc-failure]");
        const form = wrapper.querySelector(".pc-form");
        const input = wrapper.querySelector("[data-pc-input]");
        const submitBtn = wrapper.querySelector("[data-pc-submit]");
        const error = wrapper.querySelector("[data-pc-error]");
        const errorMessage = wrapper.querySelector(".pc-error-message");

        function updateButtonState() {
            const value = input && input.value ? input.value.trim() : "";

            // If no input, hide error and disable button
            if (!value) {
                if (submitBtn) submitBtn.disabled = true;
                if (error) error.hidden = true;
                return;
            }

            // Check if this matches the globally tracked failed postcode
            if (
                typeof isLastFailedPostcode === "function" &&
                isLastFailedPostcode(value)
            ) {
                debugLog(
                    "[BOTTOMSHEET UPDATE BUTTON] Detected failed postcode match, showing error",
                );
                showError(
                    "This postcode is invalid. Please try a different one.",
                );
                if (submitBtn) submitBtn.disabled = true;
                return;
            }

            // If input exists but format is invalid, show error and disable button
            if (!isValidPostcodeFormat(value)) {
                showError("Please enter a valid UK postcode format.");
                if (submitBtn) submitBtn.disabled = true;
                return;
            }

            // Valid format: enable button and hide error
            if (submitBtn) submitBtn.disabled = false;
            if (error) error.hidden = true;
        }

        function showForm() {
            if (formView) formView.hidden = false;
            if (successView) successView.hidden = true;
            if (error) error.hidden = true;
            if (input) input.focus();
        }

        function showError(message) {
            if (!error) return;
            if (errorMessage) {
                errorMessage.textContent =
                    message || "Please enter a valid postcode";
            }
            error.hidden = false;
        }

        function showSuccess() {
            if (formView) formView.hidden = true;
            if (successView) successView.hidden = false;
        }

        function showNationwide() {
            if (formView) formView.hidden = true;
            if (nationwideView) nationwideView.hidden = false;
        }

        function showFailure() {
            if (formView) formView.hidden = true;
            if (failureView) failureView.hidden = false;
        }

        function hideMessage() {
            const messageContainers = document.querySelectorAll(".postcode-msg");
            const dividerElements = document.querySelectorAll("#divider");

            messageContainers.forEach((el) => {
                el.hidden = true;
            });

            dividerElements.forEach((el) => {
                el.hidden = true;
            });
        }

        function showMessage() {
            const messageContainers = document.querySelectorAll(".postcode-msg");
            const dividerElements = document.querySelectorAll("#divider");

            messageContainers.forEach((el) => {
                el.hidden = false;
            });

            dividerElements.forEach((el) => {
                el.hidden = false;
            });
        }

        // Validate on input
        if (input) {
            input.addEventListener("input", updateButtonState);
        }

        if (form) {
            form.addEventListener("submit", async (e) => {
                e.preventDefault();
                const postcode_value =
                    input && input.value ? input.value.trim() : "";

                if (!postcode_value || postcode_value.length < 5) {
                    showError("Please enter a valid postcode");
                    return;
                }

                if (!isValidPostcodeFormat(postcode_value)) {
                    showError("Please enter a valid UK postcode format.");
                    return;
                }

                // Disable button and show loading state
                if (submitBtn) {
                    submitBtn.disabled = true;
                    submitBtn.textContent = "Checking...";
                }

                // Make API call on submit
                const result = await validatePostcodeWithAPI(
                    postcode_value,
                    showError,
                );

                debugLog("RESULT ", result);

                // Reset button text
                if (submitBtn) {
                    submitBtn.textContent = "Check options";
                }

                if (!result.valid) {
                    // Failed postcode is now tracked globally in validatePostcodeWithAPI
                    if (submitBtn) submitBtn.disabled = true;
                    return;
                }

                const outbound = getOutboundCode(postcode_value);
                var inDeliveryZone = false;
                var matchedZone = null;
                var inBlackoutZone = false;

                deliveryZones.forEach((deliveryZone) => {
                    if (deliveryZone.postcodes.includes(outbound)) {
                        inDeliveryZone = true;
                        matchedZone = deliveryZone;
                    }
                });

                inBlackoutZone = isInBlackoutZone(outbound, blackoutPostcodes);
                debugLog("[BLACKOUT CHECK] Bottomsheet outbound:", outbound, "| matched:", inBlackoutZone);

                // Store postcode data with lat/long
                const postcodeData = {
                    postcode: postcode_value,
                    latitude: result.data.latitude,
                    longitude: result.data.longitude,
                };
                localStorage.setItem(
                    "pcc-valid-pc",
                    JSON.stringify(postcodeData),
                );

                // Update header banner
                updateHeaderPostcodeBanner();

                // Update all product page pills
                const allPillTexts = document.querySelectorAll(
                    ".pc-product-pill-pc",
                );
                allPillTexts.forEach((pt) => {
                    if (pt) pt.textContent = postcode_value.toUpperCase();
                });

                // Trigger availability recalculation on all product checkers
                const productContainers = document.querySelectorAll(
                    ".pc-product-container",
                );
                productContainers.forEach((container) => {
                    const event = new CustomEvent("pcc-postcode-updated", {
                        detail: { postcodeData, postcodeValue: postcode_value },
                    });
                    container.dispatchEvent(event);
                });

                const shouldAutoClose =
                    closeOnSubmitMobile && window.innerWidth < 768;
                if (shouldAutoClose) {
                    const sheet = ensureBottomsheetInstance();
                    if (sheet) sheet.hide();
                    return;
                }
                
                // Determine which view to show based on delivery zone
                if (inBlackoutZone) {
                    debugLog("[BLACKOUT ZONE] In blackout postcode prefix");
                    hideMessage();
                    showFailure();
                } else if (inDeliveryZone) {
                    debugLog(
                        "[DELIVERY ZONE] ",
                        matchedZone.title,
                        "(",
                        matchedZone.handle,
                        ")",
                    );
                    showMessage();
                    showSuccess();
                } else if (isInPostalZone(outbound, postalZone)) {
                    debugLog("[POSTAL ZONE] Nationwide delivery available");
                    showMessage();
                    showNationwide();
                } else {
                    debugLog("[NO SERVICE] No delivery available");
                    hideMessage();
                    showFailure();
                }
            });
        }

        showForm();

        return wrapper;
    }

    function focusBottomsheetInput(content) {
        if (!content) return;
        requestAnimationFrame(() => {
            const input = content.querySelector("[data-pc-input]");
            if (input) {
                input.focus();
                if (typeof input.select === "function") input.select();
            }
        });
    }

    function parseZonesFromElement(el) {
        const zonesScript = el ? el.querySelector("[data-pc-zones]") : null;
        return zonesScript
            ? JSON.parse(zonesScript.textContent)
            : { deliveryZones: [], postalZone: null, blackoutPostcodes: [] };
    }

    function getProductNextDayDelayDays() {
        const nextDayScript = document.querySelector("[data-product-next-day]");
        if (!nextDayScript) return 1;

        try {
            const data = JSON.parse(nextDayScript.textContent || "{}");
            const availableNextDay = data && data.available_next_day;
            return availableNextDay === true ? 0 : 1;
        } catch (e) {
            debugWarn("[NEXT DAY META] Failed to parse data-product-next-day:", e);
            return 1;
        }
    }

    function isTwoDayZoneRoute(zoneHandle) {
        return zoneHandle === "zone-3" || zoneHandle === "postal-zone";
    }

    function hasStoreNextDayOverride(store) {
        if (!store) return false;
        return (
            store.next_day_override === true ||
            store.next_day_override === "true"
        );
    }

    function getAdjustedNextDayOffset(defaultOffset, options = {}) {
        const {
            contextLabel = "delivery",
            baseDays = null,
            zoneHandle = null,
            closestStoreHasNextDayOverride = false,
        } = options;

        // Business rule: do NOT add metafield delay for two-day route types
        // (zone-3, postal-zone, or collection points with next_day_override=true).
        const shouldSkipForTwoDayRoute =
            isTwoDayZoneRoute(zoneHandle) ||
            closestStoreHasNextDayOverride;

        if (shouldSkipForTwoDayRoute) {
            debugLog(
                `[NEXT DAY META] Skipping additional day for ${contextLabel}`,
                {
                    baseDays,
                    zoneHandle,
                    closestStoreHasNextDayOverride,
                },
            );
            return 0;
        }

        return defaultOffset;
    }

    function initProductChecker() {
        const container = document.querySelector(".pc-product-container");
        if (!container) return;

        ensureStylesheet(container);
        const { deliveryZones, postalZone, blackoutPostcodes } =
            parseZonesFromElement(container);

        const form = container.querySelector(".pc-form");
        const input = container.querySelector("[data-pc-input]");
        const submitBtn = container.querySelector("[data-pc-submit]");
        const error = container.querySelector("[data-pc-error]");
        const errorMessage = container.querySelector(".pc-error-message");
        const promptView = container.querySelector(".pc-product-prompt");
        const hasPcView = container.querySelector(".pc-product-has-postcode");
        const mainPill = container.querySelector(
            ".pc-product-content .pc-product-pill",
        );
        const fallbackPill = container.querySelector(
            ".pc-product-fallback-pill .pc-product-pill",
        );
        const pillText = container.querySelector(".pc-product-pill-pc");
        const deliveryItems = Array.from(
            container.querySelectorAll(".pc-product-delivery-type"),
        );

        const updateDividers = () => {
            deliveryItems.forEach((item) =>
                item.classList.remove("has-divider"),
            );
            const visible = deliveryItems.filter(
                (item) => !item.hidden && item.offsetParent !== null,
            );
            visible
                .slice(0, -1)
                .forEach((item) => item.classList.add("has-divider"));
        };

        // Function to calculate and display availability
        const updateAvailability = (postcodeData, postcodeValue) => {
            debugLog("[updateAvailability]");
            const outbound = getOutboundCode(postcodeValue);
            const nextDayDelayDays = getProductNextDayDelayDays();

            debugLog("[NEXT DAY META] Additional day offset:", nextDayDelayDays);

            // Calculate all availability options upfront
            const availability = {
                delivery: false,
                nationwide: false,
                collection: false,
                matchedZone: null,
                inPostalZone: false,
                isBlackoutPostcode: false,
                collectionData: null,
            };

            // Check delivery zone
            deliveryZones.forEach((zone) => {
                if (zone.postcodes.includes(outbound)) {
                    availability.delivery = true;
                    availability.matchedZone = zone;
                }
            });

            debugLog("[ZONE MATCH] Outbound:", outbound);
            debugLog(
                "[ZONE MATCH] Delivery zone matched:",
                availability.matchedZone
                    ? `${availability.matchedZone.title} (${availability.matchedZone.handle})`
                    : "none",
            );

            //check if postcode is in postal zone
            if (isInPostalZone(outbound, postalZone)) {
                availability.inPostalZone = true;
            }
            debugLog("[ZONE MATCH] In postal zone:", availability.inPostalZone);

            //check if postcode is in blackout
            if (isInBlackoutZone(outbound, blackoutPostcodes)) {
                availability.isBlackoutPostcode = true;
            }

            // Check postal zone - only if product is in available-nationwide collection
            const collectionsScript = document.querySelector(
                "[data-product-collections]",
            );
            if (collectionsScript) {
                debugLog("COLLECTION SCRIPT IS OK");
                try {
                    debugLog("start to try");

                    const collectionsData = JSON.parse(
                        collectionsScript.textContent,
                    );
                    const collections = collectionsData.collections || [];
                    const isNationwideProduct = collections.some(
                        (c) => c.handle === "available-nationwide",
                    );
                    debugLog("IS NATIONWIDE PRODUCT ", isNationwideProduct);

                    if (isNationwideProduct) {
                        availability.nationwide = isInPostalZone(
                            outbound,
                            postalZone,
                        );
                        debugLog(
                            " -> Product is available nationwide, postal zone check:",
                            availability.nationwide,
                        );
                    }

                    debugLog("[ZONE MATCH] Final flags", {
                        outbound,
                        delivery: availability.delivery,
                        matchedZoneHandle: availability.matchedZone
                            ? availability.matchedZone.handle
                            : null,
                        matchedZoneTitle: availability.matchedZone
                            ? availability.matchedZone.title
                            : null,
                        inPostalZone: availability.inPostalZone,
                        nationwide: availability.nationwide,
                    });
                } catch (e) {
                    debugWarn(
                        "[NATIONWIDE CHECK] Error parsing collections:",
                        e,
                    );
                }
            }

            // Check collection availability
            const priceScript = document.querySelector("[data-product-price]");
            const productPrice = priceScript
                ? JSON.parse(priceScript.textContent).price
                : 0;
            const collectionAvailability = calculateCollectionAvailability(
                postcodeData,
                productPrice,
            );
            availability.collection = collectionAvailability.available;
            availability.collectionData = collectionAvailability;

            // Log availability
            debugLog("[AVAILABILITY CHECK]", {
                delivery: availability.delivery,
                nationwide: availability.nationwide,
                collection: availability.collection,
                inPostalZone: availability.inPostalZone,
                inDeliveryZone: availability.matchedZone,
                isBlackoutPostcode: availability.blackoutPostcode,
            });

            if (availability.delivery && availability.matchedZone) {
                debugLog(
                    " -> Zone:",
                    availability.matchedZone.title,
                    "(",
                    availability.matchedZone.handle,
                    ")",
                );
            }

            const serviceState = availability.isBlackoutPostcode
                ? "blackout"
                : availability.delivery
                ? "delivery"
                : availability.nationwide
                ? "nationwide"
                : availability.collection
                ? "collection"
                : "none";

            // Check if any service is available
            const hasAnyService =
                availability.delivery ||
                availability.nationwide ||
                availability.collection;

            const updatePillText = (value) => {
                const allPillTexts = document.querySelectorAll(
                    ".pc-product-pill-pc",
                );
                allPillTexts.forEach((pt) => {
                    if (pt) pt.textContent = value.toUpperCase();
                });
            };

            const showHasPostcodeView = (value) => {
                // Show the has-postcode view (even if no service, so fallback can display)
                updatePillText(value);
                if (promptView) promptView.hidden = true;
                if (hasPcView) hasPcView.hidden = false;
                updateHeaderPostcodeBanner();
            };

            showHasPostcodeView(postcodeValue);

            // Show/hide delivery methods based on availability
            const lolaDelivery = container.querySelector(
                '[data-delivery-method="lolas-delivery"]',
            );
            const courierDelivery = container.querySelector(
                '[data-delivery-method="courier-delivery"]',
            );
            const storeCollection = container.querySelector(
                '[data-delivery-method="store-collection"]',
            );

            const toggleDeliveryMethods = () => {
                if (serviceState === "blackout") {
                    if (lolaDelivery) lolaDelivery.hidden = true;
                    if (courierDelivery) courierDelivery.hidden = true;
                    if (storeCollection) storeCollection.hidden = true;
                    return;
                }

                // Lola's Delivery - available only in delivery zones
                if (lolaDelivery) {
                    lolaDelivery.hidden = !availability.delivery;
                }

                // Courier Delivery - nationwide
                if (courierDelivery) {
                    debugLog("AVAILABILITY ", availability);
                    courierDelivery.hidden =
                        !availability.nationwide ||
                        availability.isBlackoutPostcode;
                }

                // Store Collection - available if collection is available
                if (storeCollection) {
                    storeCollection.hidden = !availability.collection;
                }
            };

            toggleDeliveryMethods();

            // Handle fallback messages
            const fallbackContainer = container.querySelector(
                "[data-fallback-container]",
            );
            const fallbackNationwide = container.querySelector(
                "[data-fallback-nationwide]",
            );
            const fallbackNoService = container.querySelector(
                "[data-fallback-no-service]",
            );
            const deliveryTypesContainer = container.querySelector(
                "[data-delivery-types]",
            );
            const contentContainer = container.querySelector(
                ".pc-product-content",
            );
            const contentTextElements = container.querySelectorAll(
                ".pc-product-content-text",
            );

            const updateDeliveryDetails = () => {
                if (availability.delivery) {
                    //show delivery method
                    //update dividers
                    updateDividers();
                    debugLog("AVAILABLE FOR DELIVERY ");

                    const priceScript = document.querySelector(
                        "[data-product-price]",
                    );
                    const productPrice = priceScript
                        ? JSON.parse(priceScript.textContent).price
                        : 0;

                    const deliveryAvailability =
                        calculateAvailabilityForDelivery(
                            postcodeData,
                            postcodeValue,
                            productPrice,
                        );
                    debugLog("[deliveryAvailability] ", deliveryAvailability);
                    if (deliveryAvailability.available) {
                        debugLog(
                            "  -> Delivery Zone:",
                            deliveryAvailability.zone.title,
                        );
                        debugLog(
                            "  -> Earliest Days:",
                            deliveryAvailability.earliestDays,
                        );
                        debugLog(
                            "  -> Delivery Price: £",
                            deliveryAvailability.deliveryPrice.toFixed(2),
                        );

                        // Calculate earliest date using helper
                        const deliveryExtraDays = getAdjustedNextDayOffset(nextDayDelayDays, {
                            contextLabel: "Lola's delivery",
                            baseDays: deliveryAvailability.earliestDays,
                            zoneHandle:
                                deliveryAvailability.zone &&
                                deliveryAvailability.zone.handle,
                        });
                        const adjustedDeliveryDays =
                            deliveryAvailability.earliestDays + deliveryExtraDays;
                        const earliestDate = new Date();
                        earliestDate.setDate(
                            earliestDate.getDate() +
                                adjustedDeliveryDays,
                        );
                        const dateString = formatDeliveryDate(earliestDate);
                        const priceString = formatPrice(
                            deliveryAvailability.deliveryPrice,
                        );

                        debugLog("  -> Date String:", dateString);
                        debugLog("  -> Price String:", priceString);

                        // Update Lola's Delivery only
                        if (lolaDelivery && !lolaDelivery.hidden) {
                            const infoElements = lolaDelivery.querySelectorAll(
                                ".pc-product-delivery-type-info",
                            );

                            // First info element = Earliest (date)
                            if (infoElements[0]) {
                                const dateContent =
                                    infoElements[0].querySelector(".content");
                                if (dateContent) {
                                    dateContent.textContent = dateString;
                                    debugLog(
                                        "  -> Updated Lola's Delivery date to:",
                                        dateString,
                                    );
                                }
                            }

                            // Second info element = Starting (price)
                            if (infoElements[1]) {
                                const priceContent =
                                    infoElements[1].querySelector(".content");
                                if (priceContent) {
                                    priceContent.textContent = priceString;
                                    debugLog(
                                        "  -> Updated Lola's Delivery price to:",
                                        priceString,
                                    );
                                }
                            }
                        }
                    }
                }
            };

            const updateCollectionDetails = () => {
                if (availability.collection) {
                    //show collection method
                    //updateDividers dividers
                    updateDividers();
                    // Use the eligible stores from collectionData (already filtered and sorted)
                    const closestStores = availability.collectionData.eligibleStores || [];
                    const productStores = closestStores.slice(0, 1);
                    debugLog("  -> Collection - Closest store:");
                    productStores.forEach((store, index) => {
                        debugLog(
                            `${index + 1}. ${
                                store.title
                            } - ${store.distance.toFixed(1)} mi - type: ${store.store_type}`,
                        );
                    });

                    // Switch collection heading/image if closest point is a locker
                    const closestStore = productStores.length > 0 ? productStores[0] : null;
                    const isLockerCollection = closestStore && closestStore.store_type === "Locker";
                    const collectionTypeTitle = container.querySelector("#collection-type-title");
                    const storeCollectionImage = container.querySelector("#store-collection-image");
                    const lockerCollectionImage = container.querySelector("#locker-collection-image");

                    if (collectionTypeTitle) {
                        collectionTypeTitle.textContent = isLockerCollection ? "Locker Collection" : "Store Collection";
                    }
                    if (storeCollectionImage) {
                        storeCollectionImage.hidden = !!isLockerCollection;
                    }
                    if (lockerCollectionImage) {
                        lockerCollectionImage.hidden = !isLockerCollection;
                    }

                    debugLog(
                        "  -> Collection Type:",
                        isLockerCollection ? "Locker Collection" : "Store Collection",
                    );

                    const nearestContainer = container.querySelector(".pc-product-delivery-type-nearest");

                    if (
                        nearestContainer &&
                        productStores &&
                        productStores.length > 0
                    ) {
                        const contentDivs =
                            nearestContainer.querySelectorAll(".content");

                        productStores.forEach((store, index) => {
                            if (contentDivs[index]) {
                                const locationSpan =
                                    contentDivs[index].querySelector(
                                        ".location",
                                    );
                                const distanceSpan =
                                    contentDivs[index].querySelector(
                                        ".distance",
                                    );

                                if (locationSpan) {
                                    let storeName = store.name
                                        ? store.name
                                        : store.title;
                                    locationSpan.textContent = storeName;
                                }
                                if (distanceSpan)
                                    distanceSpan.textContent =
                                        store.distance.toFixed(1) + " mi";
                            }
                        });

                        for (let i = 0; i < contentDivs.length; i++) {
                            if (contentDivs[i]) {
                                contentDivs[i].hidden =
                                    i >= productStores.length;
                            }
                        }
                    }

                    // Update collection price and earliest date
                    const collectionDeliveryType = container.querySelector(
                        '[data-delivery-method="store-collection"]',
                    );
                    if (collectionDeliveryType) {
                        // Check if we're past cutoff using helper
                        const cutoffTime =
                            container.dataset.cutoffTime || "16:00";
                        const isAfterCutoff = isAfterCutoffTime(cutoffTime);

                        // Calculate earliest date using helper
                        const collectionExtraDays = getAdjustedNextDayOffset(nextDayDelayDays, {
                            contextLabel: "collection",
                            baseDays: availability.collectionData.earliestDays,
                            closestStoreHasNextDayOverride:
                                hasStoreNextDayOverride(closestStore),
                        });
                        const earliestDate = calculateEarliestDate(
                            availability.collectionData.earliestDays +
                                collectionExtraDays,
                            isAfterCutoff,
                        );
                        const dateString = formatDeliveryDate(earliestDate);
                        debugLog(
                            "  -> Collection cutoff check - After cutoff:",
                            isAfterCutoff,
                        );

                        // Update DOM (date only; price text is hardcoded in markup)
                        const dateContent =
                            collectionDeliveryType.querySelector(
                                ".pc-product-delivery-type-info .content",
                            );

                        if (dateContent) dateContent.textContent = dateString;

                        debugLog("  -> Collection Date:", dateString);
                    }
                }
            };

            const updateCourierDetails = () => {
                // Handle nationwide courier delivery
                if (
                    availability.nationwide &&
                    courierDelivery &&
                    !availability.isBlackoutPostcode
                ) {
                    debugLog("[check check] ", availability);
                    debugLog("[check check check] ", courierDelivery);
                    debugLog("[NATIONWIDE AVAILABLE]");

                    // Calculate courier delivery date
                    const priceScript = document.querySelector(
                        "[data-product-price]",
                    );
                    const productPrice = priceScript
                        ? JSON.parse(priceScript.textContent).price
                        : 0;

                    // Use zone-aware timing for courier:
                    // - if postcode is also in a delivery zone, use that zone's timing
                    // - otherwise use postal-zone timing (nationwide)
                    const cutoffTime = container.dataset.cutoffTime || "16:00";
                    const isAfterCutoff = isAfterCutoffTime(cutoffTime);
                    const zoneHandle =
                        availability.matchedZone &&
                        availability.matchedZone.handle
                            ? availability.matchedZone.handle
                            : "postal-zone";

                    debugLog(
                        "[COURIER ZONE] Selected zone handle:",
                        zoneHandle,
                    );
                    debugLog(
                        "[COURIER ZONE] Source matched zone:",
                        availability.matchedZone
                            ? `${availability.matchedZone.title} (${availability.matchedZone.handle})`
                            : "none (using postal-zone)",
                    );

                    const zonePricing = calculateZonePricing(
                        zoneHandle,
                        0,
                        isAfterCutoff,
                    );
                    let courierDays = zonePricing.earliestDays;
                    const courierExtraDays = getAdjustedNextDayOffset(nextDayDelayDays, {
                        contextLabel: "courier",
                        baseDays: zonePricing.earliestDays,
                        zoneHandle,
                    });
                    courierDays += courierExtraDays;
                    debugLog(
                        "[COURIER ZONE] Base days from zone:",
                        zonePricing.earliestDays,
                        "| after cutoff:",
                        isAfterCutoff,
                    );
                    debugLog(
                        "[COURIER ZONE] next-day metafield offset:",
                        courierExtraDays,
                    );

                    debugLog(
                        "[COURIER ZONE] next_day_override adjustment disabled; using base zone days only",
                    );

                    debugLog("[COURIER ZONE] Final courierDays:", courierDays);

                    const earliestDate = calculateEarliestDate(
                        courierDays,
                        false,
                    );
                    const dateString = formatDeliveryDate(earliestDate);

                    // Get courier delivery price based on nationwide collections
                    let courierPrice = 4.99; // Default
                    const collectionsScript = document.querySelector(
                        "[data-product-collections]",
                    );

                    if (collectionsScript) {
                        try {
                            const collectionsData = JSON.parse(
                                collectionsScript.textContent,
                            );
                            const collections =
                                collectionsData.collections || [];

                            // Check for nationwide collections
                            const nationwideCollections = [
                                "cupcakes-by-post",
                                "cakes-by-post",
                                "brownies",
                            ];
                            const matchedCollection = collections.find((c) =>
                                nationwideCollections.includes(c.handle),
                            );

                            if (
                                matchedCollection &&
                                matchedCollection.min_fulfillment_price
                            ) {
                                // min_fulfillment_price is the actual shipping cost for nationwide (already in pounds)
                                courierPrice =
                                    matchedCollection.min_fulfillment_price;
                                debugLog(
                                    "[COURIER] Matched collection:",
                                    matchedCollection.handle,
                                    "| Shipping cost:",
                                    courierPrice,
                                );
                            } else {
                                debugLog(
                                    "[COURIER] No nationwide collection found, using default rate",
                                );
                            }
                        } catch (e) {
                            debugWarn(
                                "[COURIER] Error parsing collections:",
                                e,
                            );
                        }
                    }

                    const priceString = formatPrice(courierPrice);

                    debugLog("  -> Courier Date:", dateString);
                    debugLog("  -> Courier Price:", priceString);

                    // Update courier delivery DOM
                    const infoElements = courierDelivery.querySelectorAll(
                        ".pc-product-delivery-type-info",
                    );
                    if (infoElements[0]) {
                        const dateContent =
                            infoElements[0].querySelector(".content");
                        if (dateContent) dateContent.textContent = dateString;
                    }
                    if (infoElements[1]) {
                        const priceContent =
                            infoElements[1].querySelector(".content");
                        if (priceContent)
                            priceContent.textContent = priceString;
                    }
                }
            };

            const handleFallbackMessages = () => {
                const setContentVisibility = (visible) => {
                    if (!contentContainer) return;
                    contentContainer.hidden = !visible;
                    contentContainer.style.display = visible ? "" : "none";
                };

                if (serviceState === "blackout" || serviceState === "none") {
                    debugLog("[NO SERVICE OR BLACKOUT]");

                    setContentVisibility(false);
                    contentTextElements.forEach((el) => (el.hidden = true));
                    if (mainPill) mainPill.style.display = "none";
                    if (fallbackPill) fallbackPill.style.display = "flex";

                    if (deliveryTypesContainer)
                        deliveryTypesContainer.hidden = true;
                    if (fallbackContainer) fallbackContainer.hidden = false;

                    const showNationwideFallback =
                        !availability.isBlackoutPostcode &&
                        availability.inPostalZone;

                    if (fallbackNoService) {
                        fallbackNoService.hidden = showNationwideFallback;
                    }
                    if (fallbackNationwide) {
                        fallbackNationwide.hidden = !showNationwideFallback;
                    }
                    return;
                }

                setContentVisibility(true);
                contentTextElements.forEach((el) => (el.hidden = true));
                const defaultMsg = container.querySelector(
                    '.pc-product-content-text[data-msg="default"]',
                );
                const countdownMsg = container.querySelector(
                    '.pc-product-content-text[data-msg="countdown"]',
                );
                const orderByMsg = container.querySelector(
                    '.pc-product-content-text[data-msg="order-by"]',
                );
                const cutoffTime = container.dataset.cutoffTime || "16:00";
                initCountdown(
                    container,
                    cutoffTime,
                    defaultMsg,
                    countdownMsg,
                    orderByMsg,
                );
                if (mainPill) mainPill.style.display = "flex";
                if (fallbackPill) fallbackPill.style.display = "none";
                if (deliveryTypesContainer)
                    deliveryTypesContainer.hidden = false;

                if (
                    !availability.nationwide &&
                    !availability.delivery &&
                    availability.collection
                ) {
                    debugLog(
                        "[NATIONWIDE UNAVAILABLE] - Showing fallback nationwide message",
                    );
                    if (fallbackContainer) fallbackContainer.hidden = false;
                    if (fallbackNationwide) fallbackNationwide.hidden = false;
                    if (fallbackNoService) fallbackNoService.hidden = true;
                } else {
                    if (fallbackContainer) fallbackContainer.hidden = true;
                    if (fallbackNationwide) fallbackNationwide.hidden = true;
                    if (fallbackNoService) fallbackNoService.hidden = true;
                }
            };

            updateDeliveryDetails();
            updateCollectionDetails();
            updateCourierDetails();
            handleFallbackMessages();

            // Update dividers between visible delivery types
            updateDividers();

            return hasAnyService; // Service available
        };

        // Check for stored postcode data on page load
        const storedData = localStorage.getItem("pcc-valid-pc");
        if (storedData) {
            debugLog("STORED DATA");

            try {
                debugLog("TRY");
                const parsedData = JSON.parse(storedData);
                if (
                    parsedData.postcode &&
                    parsedData.latitude &&
                    parsedData.longitude
                ) {
                    // Ensure UI shows the has-postcode view immediately
                    if (promptView) promptView.hidden = true;
                    if (hasPcView) hasPcView.hidden = false;
                    const allPillTexts = document.querySelectorAll(
                        ".pc-product-pill-pc",
                    );
                    allPillTexts.forEach((pt) => {
                        if (pt && parsedData.postcode)
                            pt.textContent = parsedData.postcode.toUpperCase();
                    });
                    debugLog(
                        "[PAGE LOAD] Recalculating availability for:",
                        parsedData.postcode,
                    );
                    const postcodeData = {
                        latitude: parsedData.latitude,
                        longitude: parsedData.longitude,
                    };
                    updateAvailability(postcodeData, parsedData.postcode);
                } else {
                    // Missing lat/long: re-validate postcode to populate storage
                    const postcode = extractPostcodeFromStorage(storedData);
                    if (postcode) {
                        if (promptView) promptView.hidden = true;
                        if (hasPcView) hasPcView.hidden = false;
                        const allPillTexts = document.querySelectorAll(
                            ".pc-product-pill-pc",
                        );
                        allPillTexts.forEach((pt) => {
                            if (pt && postcode)
                                pt.textContent = postcode.toUpperCase();
                        });

                        validatePostcodeWithAPI(postcode)
                            .then((result) => {
                                if (!result || !result.valid || !result.data) {
                                    return;
                                }
                                const postcodeData = {
                                    postcode: postcode,
                                    latitude: result.data.latitude,
                                    longitude: result.data.longitude,
                                };
                                localStorage.setItem(
                                    "pcc-valid-pc",
                                    JSON.stringify(postcodeData),
                                );
                                updateAvailability(
                                    {
                                        latitude: postcodeData.latitude,
                                        longitude: postcodeData.longitude,
                                    },
                                    postcode,
                                );
                            })
                            .catch(() => {
                                // Ignore revalidation errors here
                            });
                    }
                }
            } catch (e) {
                // Old format (plain string), just show postcode without calculations
                debugLog("[PAGE LOAD] Legacy postcode format detected");
                if (promptView) promptView.hidden = true;
                if (hasPcView) hasPcView.hidden = false;
                const postcode = extractPostcodeFromStorage(storedData);
                const allPillTexts = document.querySelectorAll(
                    ".pc-product-pill-pc",
                );
                allPillTexts.forEach((pt) => {
                    if (pt && postcode) pt.textContent = postcode.toUpperCase();
                });
            }
        }

        // Listen for postcode updates from bottomsheet
        container.addEventListener("pcc-postcode-updated", (e) => {
            debugLog("[POSTCODE UPDATED EVENT] Recalculating availability");
            debugLog("[POSTCODE UPDATED EVENT] Data:", e.detail);
            const { postcodeData, postcodeValue } = e.detail;
            const dataForCalc = {
                latitude: postcodeData.latitude,
                longitude: postcodeData.longitude,
            };
            debugLog(
                "[POSTCODE UPDATED EVENT] Calling updateAvailability with:",
                dataForCalc,
                postcodeValue,
            );
            updateAvailability(dataForCalc, postcodeValue);
        });

        // Initialize countdown on product page if cutoff time is available
        const cutoffTime = container.dataset.cutoffTime;
        if (cutoffTime) {
            const defaultMsg = container.querySelector(
                '.pc-product-content-text[data-msg="default"]',
            );
            const countdownMsg = container.querySelector(
                '.pc-product-content-text[data-msg="countdown"]',
            );
            const orderByMsg = container.querySelector(
                '.pc-product-content-text[data-msg="order-by"]',
            );
            if (countdownMsg || orderByMsg) {
                initCountdown(
                    container,
                    cutoffTime,
                    defaultMsg,
                    countdownMsg,
                    orderByMsg,
                );
            }
        }

        // Handle pill recheck click
        const pills = [mainPill, fallbackPill].filter((p) => p);
        pills.forEach((pill) => {
            pill.addEventListener("click", () => {
                // Check if desktop (viewport width >= 768px)
                const isDesktop = window.innerWidth >= 768;

                if (isDesktop) {
                    // Desktop: Show the inline prompt instead of bottomsheet
                    if (promptView) promptView.hidden = false;
                    if (hasPcView) hasPcView.hidden = true;
                    if (input) {
                        input.value = "";
                        input.focus();
                    }
                    if (error) error.hidden = true;
                    if (submitBtn) submitBtn.disabled = true;
                } else {
                    // Mobile: Open bottomsheet
                    const template = document.getElementById(
                        "postcode-checker-template",
                    );
                    if (template) {
                        ensureStylesheet(template);
                        const sheet = ensureBottomsheetInstance();
                        if (sheet) {
                            const content = buildContent(template, {
                                closeOnSubmitMobile: true,
                            });
                            const primaryColor =
                                template.dataset.pcPrimaryColor || "#0062A6";
                            const secondaryColor =
                                template.dataset.pcSecondaryColor || "#E0EFF9";
                            sheet.show(content, {
                                primaryColor: primaryColor,
                                secondaryColor: secondaryColor,
                            });
                            focusBottomsheetInput(content);
                        }
                    }
                }
            });
            pill.addEventListener("keydown", (e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    pill.click();
                }
            });
        });

        const showError = (message) => {
            // debugLog("[PRODUCT showError] Called with message:", message);
            // debugLog("[PRODUCT showError] error element:", error);
            // debugLog("[PRODUCT showError] errorMessage element:", errorMessage);

            if (!error) {
                debugLog("[PRODUCT showError] No error container found");
                return;
            }

            if (errorMessage) {
                errorMessage.textContent =
                    message || "Please enter a valid postcode";
                debugLog(
                    "[PRODUCT showError] Set error message text to:",
                    errorMessage.textContent,
                );
            } else {
                // debugLog("[PRODUCT showError] No errorMessage element found, setting error innerHTML directly");
                error.innerHTML = `<span class="pc-error-message">${
                    message || "Please enter a valid postcode"
                }</span>`;
            }

            error.hidden = false;
            // debugLog("[PRODUCT showError] Error shown");
        };

        const updateButtonState = () => {
            const value = input && input.value ? input.value.trim() : "";

            // If no input, hide error and disable button
            if (!value) {
                if (submitBtn) submitBtn.disabled = true;
                if (error) error.hidden = true;
                return;
            }

            // Check if this matches the globally tracked failed postcode
            if (
                typeof isLastFailedPostcode === "function" &&
                isLastFailedPostcode(value)
            ) {
                debugLog(
                    "[PRODUCT UPDATE BUTTON] Detected failed postcode match, showing error",
                );
                showError(
                    "This postcode is invalid. Please try a different one.",
                );
                if (submitBtn) submitBtn.disabled = true;
                return;
            }

            // If input exists but format is invalid, show error and disable button
            if (!isValidPostcodeFormat(value)) {
                showError("Please enter a valid UK postcode format.");
                if (submitBtn) submitBtn.disabled = true;
                return;
            }

            // Valid format: enable button and hide error
            if (submitBtn) submitBtn.disabled = false;
            if (error) error.hidden = true;
        };

        if (input) {
            input.addEventListener("input", updateButtonState);
        }
        updateDividers();

        if (form) {
            form.addEventListener("submit", async (e) => {
                e.preventDefault();
                const postcodeValue =
                    input && input.value ? input.value.trim() : "";

                if (!postcodeValue || postcodeValue.length < 5) {
                    showError("Please enter a valid postcode");
                    return;
                }

                if (!isValidPostcodeFormat(postcodeValue)) {
                    showError("Please enter a valid UK postcode format.");
                    return;
                }

                if (submitBtn) {
                    submitBtn.disabled = true;
                    submitBtn.textContent = "Checking...";
                }

                const result = await validatePostcodeWithAPI(
                    postcodeValue,
                    showError,
                );

                if (submitBtn) {
                    submitBtn.textContent = "Check";
                }

                if (!result.valid) {
                    // Failed postcode is now tracked globally in validatePostcodeWithAPI
                    if (submitBtn) submitBtn.disabled = true;
                    return;
                }

                // Store postcode data with lat/long for future page loads
                const postcodeData = {
                    postcode: postcodeValue,
                    latitude: result.data.latitude,
                    longitude: result.data.longitude,
                };

                // Calculate and update availability
                const hasService = updateAvailability(
                    result.data,
                    postcodeValue,
                );

                if (!hasService) {
                    debugLog("HAS NO SERVICE ");
                    // No service available at all - fallback UI already shows the message
                    if (error) error.hidden = true;
                    return;
                }

                // Store the postcode data for future page loads
                localStorage.setItem(
                    "pcc-valid-pc",
                    JSON.stringify(postcodeData),
                );
                updateHeaderPostcodeBanner();
            });
        }
    }

    function initDeliveryInfoModal() {
        const infoTriggers = document.querySelectorAll(
            ".pc-delivery-info-trigger",
        );

        if (!infoTriggers.length) {
            return;
        }

        const sheet = ensureBottomsheetInstance();
        if (!sheet) {
            debugWarn("Bottomsheet instance not available");
            return;
        }

        const renderCollectionStores = (content) => {
            if (!content) return;

            const storesContainer = content.querySelector("[data-di-stores]");
            if (!storesContainer) return;

            storesContainer.innerHTML = "";

            const storedData = localStorage.getItem("pcc-valid-pc");
            if (!storedData) {
                const msg = document.createElement("p");
                msg.textContent = "Enter your postcode to see nearby stores.";
                storesContainer.appendChild(msg);
                return;
            }

            let parsedData = null;
            try {
                parsedData = JSON.parse(storedData);
            } catch (e) {
                parsedData = null;
            }

            if (!parsedData || !parsedData.latitude || !parsedData.longitude) {
                const msg = document.createElement("p");
                msg.textContent = "Enter your postcode to see nearby stores.";
                storesContainer.appendChild(msg);
                return;
            }

            const priceScript = document.querySelector("[data-product-price]");
            const productPrice = priceScript
                ? JSON.parse(priceScript.textContent).price
                : 0;

            const availability = calculateCollectionAvailability(
                {
                    latitude: parsedData.latitude,
                    longitude: parsedData.longitude,
                },
                productPrice,
            );

            if (!availability || !availability.available) {
                const msg = document.createElement("p");
                msg.textContent = "No nearby stores available for collection.";
                storesContainer.appendChild(msg);
                return;
            }

            const closestStores = (availability.eligibleStores || []).slice(0,3);
            closestStores.forEach((store, index) => {
                const item = document.createElement("div");
                item.className = "delivery-info-store";

                const number = document.createElement("span");
                number.className = "number";
                number.textContent = `${index + 1}.`;

                const location = document.createElement("span");
                location.className = "location";
                // if has store name use that otherwise use title as fallback
                let storeName = store.name ? store.name : store.title;
                location.textContent = storeName;

                const separator = document.createElement("span");
                separator.className = "seperator";
                separator.textContent = "•";

                const distance = document.createElement("span");
                distance.className = "distance";
                distance.textContent = store.distance.toFixed(1) + " mi";

                item.appendChild(number);
                item.appendChild(location);
                item.appendChild(separator);
                item.appendChild(distance);
                storesContainer.appendChild(item);
            });
        };

        infoTriggers.forEach((trigger) => {
            trigger.addEventListener("click", (event) => {
                event.preventDefault();
                const method = trigger.dataset.deliveryMethod;

                // Find the template for this delivery method
                const templateId = `delivery-info-${method}`;
                const template = document.getElementById(templateId);

                if (!template) {
                    debugWarn(`Template not found: ${templateId}`);
                    return;
                }

                // Clone template content
                const content = template.content
                    .cloneNode(true)
                    .querySelector(".delivery-info-content");

                if (method === "store-collection") {
                    renderCollectionStores(content);
                }

                // Get colors from template
                const primaryColor =
                    template.dataset.diPrimaryColor || "#DE0076";
                const secondaryColor =
                    template.dataset.diSecondaryColor || "#FAF6F9";

                sheet.show(content, {
                    primaryColor: primaryColor,
                    secondaryColor: secondaryColor,
                });
            });
        });
    }

    document.addEventListener("DOMContentLoaded", () => {
        const template = document.getElementById("postcode-checker-template");
        const triggers = document.querySelectorAll("[data-pc-open]");

        if (template && triggers.length) {
            ensureStylesheet(template);

            const sheet = ensureBottomsheetInstance();
            if (sheet) {
                triggers.forEach((trigger) => {
                    trigger.addEventListener("click", (event) => {
                        event.preventDefault();
                        const content = buildContent(template);
                        const primaryColor =
                            template.dataset.pcPrimaryColor || "#0062A6";
                        const secondaryColor =
                            template.dataset.pcSecondaryColor || "#E0EFF9";
                        sheet.show(content, {
                            primaryColor: primaryColor,
                            secondaryColor: secondaryColor,
                        });
                        focusBottomsheetInput(content);
                    });
                });
            }
        }

        // Product page inline postcode checker
        initProductChecker();

        // Delivery info modal triggers
        initDeliveryInfoModal();

        // Sync header banner state and announcement
        updateHeaderPostcodeBanner();
        initDeliveryAnnouncement();
        initAnnouncementClose();
    });
})();
